import { useMemo, useState } from "react";
import { Badge, Box, Flex, Link, Text } from "@radix-ui/themes";

import {
  formatRelativeTime,
  previewText,
  statusBadgeColor,
} from "@/components/planner-activity-utils";

const PREVIEW_TEXT_LENGTH = 160;

export type PanelPullRequest = {
  key: string;
  repoName: string;
  title: string;
  description: string;
  url: string | null;
  status: string;
  updated_at: string | null;
};

export type PanelCommit = {
  key: string;
  repoName: string;
  title: string;
  description: string;
  url: string | null;
  author: string;
  committed_at: string | null;
};

export function PlannerRepositoryActivity({
  pullRequests,
  commits,
}: {
  pullRequests: PanelPullRequest[];
  commits: PanelCommit[];
}) {
  return (
    <Flex gap="3" wrap="wrap">
      <ActivityColumn type="pull_request" title="Recent Pull Requests" items={pullRequests} />
      <ActivityColumn type="commit" title="Recent Commits" items={commits} />
    </Flex>
  );
}

function ActivityColumn({
  type,
  title,
  items,
}: {
  type: "pull_request" | "commit";
  title: string;
  items: PanelPullRequest[] | PanelCommit[];
}) {
  const [expandedItemKeys, setExpandedItemKeys] = useState<Record<string, true>>({});
  const emptyText =
    type === "pull_request" ? "No recent pull requests found." : "No recent commits found.";

  const sortedItems = useMemo(() => items, [items]);

  return (
    <Box style={{ flex: "1 1 360px", minWidth: "300px" }}>
      <Text size="1" weight="medium" color="gray">
        {title}
      </Text>
      {sortedItems.length > 0 ? (
        <Flex direction="column" gap="1" mt="2">
          {sortedItems.map((item) => {
            const expanded = Boolean(expandedItemKeys[item.key]);
            const description = item.description || "";
            const preview = previewText(description, PREVIEW_TEXT_LENGTH);
            const hasTrailingEllipsis = /(\.\.\.|…)\s*$/.test(description);
            const showToggle = preview !== description || hasTrailingEllipsis;

            return (
              <Box key={item.key} className="my-projects-activity-item">
                <Flex align="center" gap="2" wrap="wrap">
                  <Text size="1" className="planner-activity-repo-label">
                    {item.repoName || "Unknown repo"}
                  </Text>
                  {item.url ? (
                    <Link href={item.url} target="_blank" rel="noreferrer" size="2">
                      {item.title || (type === "pull_request" ? "Untitled PR" : "Untitled commit")}
                    </Link>
                  ) : (
                    <Text size="2">
                      {item.title || (type === "pull_request" ? "Untitled PR" : "Untitled commit")}
                    </Text>
                  )}
                  {type === "pull_request" ? (
                    <Badge
                      size="1"
                      color={statusBadgeColor((item as PanelPullRequest).status || "open")}
                    >
                      {(item as PanelPullRequest).status || "open"}
                    </Badge>
                  ) : (
                    <Text size="1" color="gray">
                      {(item as PanelCommit).author || "Unknown author"}
                    </Text>
                  )}
                  <Text size="1" color="gray">
                    {formatRelativeTime(
                      type === "pull_request"
                        ? (item as PanelPullRequest).updated_at || null
                        : (item as PanelCommit).committed_at || null,
                    )}
                  </Text>
                </Flex>
                {description ? (
                  <Text size="1" color="gray">
                    {expanded ? description : preview}
                    {expanded && preview === description && hasTrailingEllipsis && item.url ? (
                      <>
                        {" "}
                        <Link href={item.url} target="_blank" rel="noreferrer" size="1">
                          Open full message
                        </Link>
                      </>
                    ) : null}
                    {showToggle ? (
                      <>
                        {" "}
                        <button
                          type="button"
                          className="planner-activity-more-button"
                          aria-expanded={expanded}
                          onClick={() => {
                            setExpandedItemKeys((current) => {
                              if (current[item.key]) {
                                const next = { ...current };
                                delete next[item.key];
                                return next;
                              }
                              return { ...current, [item.key]: true };
                            });
                          }}
                        >
                          {expanded ? "Less" : "More"}
                        </button>
                      </>
                    ) : null}
                  </Text>
                ) : null}
              </Box>
            );
          })}
        </Flex>
      ) : (
        <Text size="1" color="gray" mt="2">
          {emptyText}
        </Text>
      )}
    </Box>
  );
}
